import { HttpStatusCode } from "axios";
import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import { EmergencyErrorCode } from "../../../../consts/emergency";

import {
  EmergencyQrJsonCode,
  EmergencySituationType,
  GpsCallTimeoutMS,
} from "../../../../types/emergency";

import {
  getUncertSituation,
  patchUncertGps,
  patchUncertQr,
} from "../../../../services/emergencyServices";

import useSimplePopup from "../../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../../components/Layout/StickyHeader";
import ExpiredSituation from "../../Situation/Expired";
import NaverMapWithSkeleton from "../../NaverMapWithSkeleton";
import SituationStatusButtonGroup from "../../StatusButtonGroup";
import CommonPopup from "../../../../components/Popup";
import CommonButton from "../../../../components/Button";
import QrCodeReader from "../../../../components/QrCodeReader";

import QrImage from "../../../../assets/img/emergency/ee-qr.png";
import PrimaryButton from "../../../../components/Button/Primary";
import PinchZoomImage from "../../../../components/PinchZoomImage";
import { findOptimizedImgUrl } from "../../../../libs/image";

type Props = {};

export default function UncertSituation(props: Props) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [simplePopupComponent, { open, openError }] = useSimplePopup({});
  const [popup, setPopup] = useState<
    "" | "qrSuccess" | "GpsFail" | "GpsDetail" | "anotherMethod"
  >(""); // QR 인증 팝업 상태 추가
  const [situation, setSituation] = useState<EmergencySituationType>();
  const [isShelterImgOpen, setIsShelterImgOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({
    isLoading: isLoading,
  });
  const [isExpired, setIsExpired] = useState(false);
  const [mode, setMode] = useState<"QR" | undefined>(undefined);

  const handleGps = () => {
    setIsLoading(true);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            if (!params.id) {
              throw new Error("상황 정보가 없습니다.");
            }

            const response = await patchUncertGps(params.id, {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
            if (response.status === HttpStatusCode.Ok) {
              setPopup("GpsDetail");
            } else {
              setPopup("GpsFail");
            }
          } catch (e) {
            if (popup === "GpsFail") {
              openError(e, "GPS 인증에 실패하였습니다.");
            } else {
              setPopup("GpsFail");
            }
          } finally {
            setIsLoading(false);
          }
        },
        (error) => {
          setPopup("GpsFail");
          switch (error.code) {
            case error.PERMISSION_DENIED:
              open(
                "GPS 사용 권한이 거부되었습니다.\n브라우저 설정에서 위치 권한을 허용해주세요."
              );
              break;
            case error.POSITION_UNAVAILABLE:
              open("위치 정보를 사용할 수 없습니다.");
              break;
            case error.TIMEOUT:
              open("위치 정보 요청 시간이 초과되었습니다.");
              break;
            default:
              open("GPS 정보를 가져올 수 없습니다.");
          }
          setIsLoading(false);
        },
        { timeout: GpsCallTimeoutMS }
      );
    } else {
      open("GPS 정보를 가져올 수 없습니다.");
      setIsLoading(false);
    }
  };

  const handleQrPatchCallback = async (qrResult: EmergencyQrJsonCode) => {
    try {
      if (!params.id) {
        throw new Error("상황 정보가 없습니다.");
      }

      const response = await patchUncertQr(params.id, qrResult);
      if (response.status === HttpStatusCode.Ok) {
        setPopup("qrSuccess");
      } else {
        openError(response, "QR 인증에 실패하였습니다.");
      }
    } catch (e) {
      openError(e, "QR 인증에 실패하였습니다.");
    }
  };

  // useEffect(() => {
  //   if (qrResult.isLoading === false) {
  //     if (qrResult.result) {
  //       const qrJson = JSON.parse(qrResult.result);
  //       handleQrPatchCallback({
  //         shelterId: qrJson["shelterId"],
  //         createdAdminId: qrJson["createdAdminId"],
  //       });
  //     } else {
  //       openError(new Error("QR 인증 실패"), "QR 인증에 실패하였습니다.");
  //       setIsQrLoading(false);
  //     }
  //   }
  // }, [qrResult]);

  const loadSituation = async () => {
    if (!params.id) return;

    setIsLoading(true);
    try {
      const response = await getUncertSituation(params.id);
      if (response.status === HttpStatusCode.Ok) {
        if (response.data.status === "ENDED") {
          navigate(`/uncert-evacuation/done/${params.id}`);
          return;
        } else {
          setSituation(response.data);
        }
      }
    } catch (e: any) {
      if (e?.response?.data?.code === EmergencyErrorCode.EXPIRED) {
        setIsExpired(true);
      }
      openError(e, "상황 정보를 불러오지 못했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSituation();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadSituation();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const moveToUncertResult = () => {
    navigate(`/uncert-evacuation/evacuation-result`, {
      state: {
        situation,
        referer: location?.pathname ?? `/uncert-evacuation/${params.id}`,
      },
    });
  };

  const handleClickShelterImg = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsShelterImgOpen(true);
  };
  const handleCloseShelterImg = (e: React.MouseEvent) => {
    setIsShelterImgOpen(false);
  };

  return (
    <>
      {loadingComponent}
      {isExpired ? (
        <ExpiredSituation />
      ) : (
        <>
          <div className="px-5 w-full">
            <StickyHeader title="비상 대피" />
          </div>
          <div className="min-h-screen z-30 bg-white w-full">
            {mode === undefined && (
              <NaverMapWithSkeleton
                situation={situation}
                cert={false}
                handleClickShelterImg={handleClickShelterImg}
              />
            )}
            <SituationStatusButtonGroup
              situation={situation}
              handleGps={handleGps}
              refresh={loadSituation}
            />

            <CommonPopup
              isOpen={popup === "GpsDetail"}
              onClose={() => moveToUncertResult()}
              padding={0}
              title="GPS 인증 성공"
              unUseHorizonRule
            >
              <div className="flex flex-col items-center">
                <div className="p-5 pt-0">
                  <div className="flex flex-col gap-3 py-2.5 items-center text-center">
                    GPS 인증이 완료되었습니다.
                  </div>
                </div>
                <div className="flex w-full">
                  <CommonButton
                    className="rounded-none flex-1 h-bottom-button"
                    onClick={() => moveToUncertResult()}
                  >
                    확인
                  </CommonButton>
                </div>
              </div>
            </CommonPopup>
            <CommonPopup
              isOpen={popup === "GpsFail"}
              onClose={() => setPopup("")}
              padding={0}
              title="GPS 인증 실패"
              unUseHorizonRule
            >
              <div className="flex flex-col items-center gap-5">
                <div>
                  <img
                    src={findOptimizedImgUrl(situation?.shelterImg?.items)}
                    alt="shelterImg"
                    className="w-[51vw] h-[32vw] object-fill rounded-[10px]"
                  />
                </div>
                <div className="flex flex-col gap-3 items-center">
                  <div className="text-base font-bold text-lg leading-[19.36px]">
                    {situation?.shelterName}
                  </div>
                  <div className="text-sm leading-[16.94px]">
                    {situation?.shelterDescription}
                  </div>
                </div>
                <div className="flex flex-col gap-3 items-center px-2">
                  <div className="text-xl leading-[26px] font-bold pb-4">
                    대피장소로 이동 후 다시 시도해주세요.
                  </div>
                </div>
                <PrimaryButton
                  className="rounded-[15px] w-[calc(100%-100px)] h-[40px] font-bold text-lg"
                  onClick={() => {
                    setPopup("anotherMethod");
                  }}
                >
                  다른 방식으로 인증하기
                </PrimaryButton>

                <div className="flex w-full">
                  <CommonButton
                    variant="secondary"
                    className="rounded-none flex-1 h-bottom-button"
                    onClick={() => setPopup("")}
                  >
                    닫기
                  </CommonButton>
                  <CommonButton
                    className="rounded-none flex-1 h-bottom-button"
                    onClick={handleGps}
                  >
                    다시시도
                  </CommonButton>
                </div>
              </div>
            </CommonPopup>

            <CommonPopup
              isOpen={popup === "qrSuccess"}
              onClose={() => moveToUncertResult()}
              padding={0}
              title="QR 인증 성공"
              unUseHorizonRule
            >
              <div className="flex flex-col items-center">
                <div className="p-5 pt-0">
                  <div className="flex flex-col gap-3 py-2.5 items-center text-center">
                    QR 인증이 완료되었습니다.
                  </div>
                </div>
                <div className="flex w-full">
                  <CommonButton
                    className="rounded-none flex-1 h-bottom-button"
                    onClick={() => moveToUncertResult()}
                  >
                    확인
                  </CommonButton>
                </div>
              </div>
            </CommonPopup>

            <CommonPopup
              isOpen={popup === "anotherMethod"}
              onClose={() => setPopup("")}
              padding={0}
              title="대피 완료 인증"
              unUseHorizonRule
            >
              <div className="flex flex-col items-center gap-7">
                <div className="flex gap-3 w-[calc(100vw-40px)]">
                  <div
                    className="flex-1 border rounded-[0.75px] flex flex-col gap-2 items-center whitespace-pre-line text-center p-3 text-sm leading-[17px] font-medium"
                    onClick={() => {
                      setMode("QR");
                    }}
                  >
                    <img src={QrImage} alt="qr" className="w-[40px] h-[40px]" />
                    QR 인증
                  </div>
                </div>

                <div className="flex w-full">
                  <CommonButton
                    variant="secondary"
                    className="rounded-none flex-1 h-bottom-button"
                    onClick={() => setPopup("")}
                  >
                    닫기
                  </CommonButton>
                </div>
              </div>
            </CommonPopup>

            {mode === "QR" && (
              <QrCodeReader
                onClose={() => setMode(undefined)}
                onResult={(result) => {
                  console.log("result", result, JSON.parse(result));
                  handleQrPatchCallback(JSON.parse(result));
                  setMode(undefined);
                }}
              />
            )}
          </div>
          {simplePopupComponent}
          {situation &&
            situation.shelterImg &&
            situation.shelterImg.items.length > 0 && (
              <PinchZoomImage
                imageList={[
                  findOptimizedImgUrl(situation?.shelterImg?.items) as string,
                ]}
                isOpen={isShelterImgOpen}
                onClose={handleCloseShelterImg}
              />
            )}
        </>
      )}
    </>
  );
}
